/* @flow */

import { useDispatch } from 'react-redux';
import {
  isBraindateCancelled,
  normalizeBraindate,
} from '@e180/domain/lib/braindate/util';
import { getEventId, getEventSocketURL } from '@e180/domain/lib/event/util';
import { areMembershipSlackNotificationsEnabled } from '@e180/domain/lib/membership/util';
import { getUserId } from '@e180/domain/lib/user/util';
import {
  topicModerationRejectedStatus,
  topicModerationVerifiedStatus,
} from '@e180/domain/lib/topic/setting';
import { isObject } from '@e180/util/lib/type';

import {
  braindateCreated,
  braindateChanged,
  braindateFollowup,
  groupBraindateFollowup,
  braindateReminder,
  groupBraindateReminder,
  braindateEarlyReminder,
  groupBraindateEarlyReminder,
  braindateCheckIn,
  calendarSyncStatusMessage,
  moderationState,
  notificationCountUpdate,
} from 'src/shared/app/base/constant/socketConstants';
import useSocket from 'src/shared/app/base/hook/useSocket';
import {
  showVirtualBraindateNotification,
  showVirtualBraindateEarlyReminder,
} from 'plugin/virtual-braindate/action/uiVirtualBraindateActions';
import useVirtualBraindate from 'plugin/virtual-braindate/hook/useVirtualBraindate';
import { showCheckInReminder } from 'src/shared/app/check-in/action/uiCheckInActions';
import { getBraindateReminderNotificationId } from 'src/shared/app/check-in/util/checkInUtil';
import useFeatureDecisions from 'src/shared/app/feature/hook/useFeatureDecisions';
import {
  showModerationAcceptedBanner,
  showModerationRejectedBanner,
  showModerationAcceptedAndSlackBanner,
} from 'src/shared/app/moderation/action/moderationActions';
import { setBraindateNotificationCount } from 'src/shared/app/notification/action/appNotificationActions';
import { dismissNotification } from 'src/shared/app/notification/action/uiNotificationActions';
import { showPostBraindateFeedback } from 'src/shared/app/post-braindate/action/postBraindateActions';
import { patchEntities } from 'src/shared/domain/base/action/entityActions';
import { createBraindate } from 'src/shared/domain/braindate/action/braindateActions';
import useEvent from 'src/shared/domain/event/hook/useEvent';
import { updateCalendarSyncStatus } from 'src/shared/domain/membership/action/membershipActions';
import useMembership from 'src/shared/domain/membership/hook/useMembership';
import { patchTopicModeration } from 'src/shared/domain/topic/action/topicActions';
import { fetchUserActivity } from 'src/shared/domain/user/action/userActions';
import useSelf from 'src/shared/domain/user/hook/useSelf';

import type { Braindate } from '@e180/domain/lib/braindate/type';

export default function useGlobalNotifications() {
  const event = useEvent();
  const user = useSelf();
  const membership = useMembership();
  const dispatch = useDispatch();
  const isVirtualBraindateEnabled = useVirtualBraindate();
  const {
    isSelfCheckInEnabled,
    isSlackIntegrationEnabled,
  } = useFeatureDecisions();
  // FIXME: don't hardcode `urls`
  const url = 'urls' in event ? getEventSocketURL(event) : '';

  useSocket(url, ({ trigger, data }) => {
    switch (trigger) {
      case notificationCountUpdate:
        dispatch(setBraindateNotificationCount(data.value));

        break;

      case braindateCreated: {
        const braindate = (data: Braindate);

        // State update

        // Add the braindate data to the state and update it in `app.braindates.all.result`
        // so that the notification count is updated
        // FIXME: handle notifications through another socket message
        dispatch(createBraindate(braindate));

        break;
      }

      case braindateChanged: {
        const braindate = (data: Braindate);

        // State update

        dispatch(patchEntities(normalizeBraindate(braindate).entities));

        // Discard check-in notification if braindate was cancelled
        if (isBraindateCancelled(braindate)) {
          dispatch(
            dismissNotification(getBraindateReminderNotificationId(braindate)),
          );
        }

        break;
      }

      case braindateReminder: {
        const braindate: Braindate = (data: Braindate);

        if (isSelfCheckInEnabled) {
          dispatch(patchEntities(normalizeBraindate(braindate).entities));
          dispatch(showCheckInReminder(braindate));
        } else if (isVirtualBraindateEnabled) {
          dispatch(patchEntities(normalizeBraindate(braindate).entities));
          dispatch(showVirtualBraindateNotification(braindate));
        }

        break;
      }

      case braindateEarlyReminder: {
        const braindate: Braindate = (data: Braindate);

        if (isVirtualBraindateEnabled) {
          dispatch(patchEntities(normalizeBraindate(braindate).entities));
          dispatch(showVirtualBraindateEarlyReminder(braindate));
        }

        break;
      }

      case groupBraindateReminder: {
        const braindate: Braindate = (data: Braindate);

        if (isSelfCheckInEnabled) {
          dispatch(patchEntities(normalizeBraindate(braindate).entities));
          dispatch(showCheckInReminder(braindate));
        } else if (isVirtualBraindateEnabled) {
          dispatch(patchEntities(normalizeBraindate(braindate).entities));
          dispatch(showVirtualBraindateNotification(braindate));
        }

        break;
      }

      case groupBraindateEarlyReminder: {
        const braindate: Braindate = (data: Braindate);

        if (isVirtualBraindateEnabled) {
          dispatch(patchEntities(normalizeBraindate(braindate).entities));
          dispatch(showVirtualBraindateEarlyReminder(braindate));
        }

        break;
      }

      // Someone else has checked in
      case braindateCheckIn: {
        const braindate = (data: Braindate);

        dispatch(patchEntities(normalizeBraindate(braindate).entities));

        break;
      }

      case calendarSyncStatusMessage: {
        dispatch(updateCalendarSyncStatus(isObject(data) ? data : {}));

        if (user) {
          // Fetch update authenticated user's activity
          dispatch(fetchUserActivity(getEventId(event), getUserId(user)));
        }

        break;
      }

      case moderationState: {
        const { state } = data;

        // If the topic status is rejected, we show the banner and patch the topic status and reason
        // If the topic is accepted, we check is slack is enabled to display the proper notification:
        // Posted or posted and shared on slack

        if (state === topicModerationRejectedStatus) {
          dispatch(patchTopicModeration(data));
          dispatch(showModerationRejectedBanner());
        } else if (state === topicModerationVerifiedStatus && membership) {
          if (
            isSlackIntegrationEnabled &&
            areMembershipSlackNotificationsEnabled(membership)
          ) {
            dispatch(showModerationAcceptedAndSlackBanner());
          } else {
            dispatch(showModerationAcceptedBanner());
          }
        }

        break;
      }

      case braindateFollowup: {
        const braindate = (data: Braindate);

        dispatch(showPostBraindateFeedback(braindate));

        break;
      }

      case groupBraindateFollowup: {
        const braindate = (data: Braindate);

        dispatch(showPostBraindateFeedback(braindate));

        break;
      }
    }
  });
}
